import { FC, ReactElement } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';
import { getShippingPrice } from '../shippingHelpers';
import { shippingModal as messages } from '../shippingMessages';

import styles from './ShippingDetailsQuotes.scss';

import { ShippingDetailsQuotesTableRow_viewer$key } from './__generated__/ShippingDetailsQuotesTableRow_viewer.graphql';
import { ShippingDetailsQuotesTableRow_item$key } from './__generated__/ShippingDetailsQuotesTableRow_item.graphql';
import {
    ShippingDetailsQuotesTableRow_shipmentQuote$key,
    ShippingDetailsQuotesTableRow_shipmentQuote$data,
} from './__generated__/ShippingDetailsQuotesTableRow_shipmentQuote.graphql';

// constants
type ShippingDetailsQuotesTableRowProps = {
    currency: string;
    shipmentQuote: ShippingDetailsQuotesTableRow_shipmentQuote$key | null;
    viewer: ShippingDetailsQuotesTableRow_viewer$key;
    item: ShippingDetailsQuotesTableRow_item$key;
    isBuyerArrangedRow?: boolean;
};

type SalePriceRowProps = {
    quote: ShippingDetailsQuotesTableRow_shipmentQuote$data;
    currency: string;
    showShippingDiscounts: boolean;
};

const cellClass = styles.quotesTableBodyCell;
const priceCellClass = classnames(styles.quotesTableBodyCell, styles.priceData);

const SalePriceRow: FC<SalePriceRowProps> = ({ quote, currency, showShippingDiscounts }) => {
    const fullPrice = quote?.subTotalAmount?.convertedAmountList || [];
    const endPrice = quote?.buyerTotalAmount?.convertedAmountList || [];
    const fullPriceAmount = fullPrice[0]?.amount;
    const endPriceAmount = endPrice[0]?.amount;
    const isSale =
        showShippingDiscounts &&
        (!!fullPriceAmount || fullPriceAmount === 0) &&
        (!!endPriceAmount || endPriceAmount === 0) &&
        fullPriceAmount > endPriceAmount;

    return (
        <td
            data-tn="ship-price-value"
            className={classnames(priceCellClass, { [styles.discountPriceRow]: isSale })}
        >
            {isSale && (
                <span className={styles.discountedPrice}>
                    {getShippingPrice({
                        convertedAmountList: fullPrice,
                        currency,
                    })}
                </span>
            )}
            <span className={isSale ? styles.salePrice : ''}>
                {getShippingPrice({
                    convertedAmountList: endPrice,
                    currency,
                })}
            </span>
        </td>
    );
};

export const ShippingDetailsQuotesTableRow: FC<ShippingDetailsQuotesTableRowProps> = ({
    currency,
    shipmentQuote: shipmentQuoteRef,
    viewer: viewerRef,
    item: itemRef,
    isBuyerArrangedRow,
}) => {
    const viewer = useFragment(
        graphql`
            fragment ShippingDetailsQuotesTableRow_viewer on Viewer {
                showShippingDiscounts: featureFlag(feature: "subsidizedShippingPDP")
            }
        `,
        viewerRef
    );
    const item = useFragment(
        graphql`
            fragment ShippingDetailsQuotesTableRow_item on Item {
                address {
                    itemAddress: displayCityStateCountry(fullCountryName: true)
                }
            }
        `,
        itemRef
    );
    const shipmentQuote = useFragment(
        graphql`
            fragment ShippingDetailsQuotesTableRow_shipmentQuote on ShipmentQuote {
                serviceId
                isLocalPickup
                handlingTimeDisplay
                subTotalAmount {
                    amount
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                buyerTotalAmount {
                    amount
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                buyerServiceMethodDisplay
            }
        `,
        shipmentQuoteRef
    );
    const { showShippingDiscounts = false } = viewer || {};
    const { isLocalPickup, serviceId: quoteId, handlingTimeDisplay } = shipmentQuote || {};
    const address = item?.address?.itemAddress;
    let methodLabel: ReactElement | string = '';
    if (isLocalPickup) {
        methodLabel = address
            ? messages.localPickupLabelWithAddress(address)
            : messages.localPickupLabel;
    } else if (isBuyerArrangedRow) {
        methodLabel = messages.buyerArrangedShippingLabel;
    } else if (shipmentQuote) {
        methodLabel = messages.serviceMethod(shipmentQuote);
    }

    return (
        <tr
            data-tn={`quote-${quoteId || 'buyer-arranged'}`}
            key={quoteId || 'buyer-arranged'}
            className={styles.shippingQuotesTableRow}
        >
            <td data-tn="ship-method-value" className={classnames(cellClass, styles.methodCell)}>
                {methodLabel}
            </td>
            <td data-tn="ship-time-value" className={classnames(cellClass, styles.timePeriodCell)}>
                {handlingTimeDisplay || ''}
            </td>
            {shipmentQuote && !isLocalPickup ? (
                <SalePriceRow
                    currency={currency}
                    showShippingDiscounts={showShippingDiscounts || false}
                    quote={shipmentQuote}
                />
            ) : (
                <td
                    data-tn="ship-price-value"
                    className={classnames(priceCellClass, styles.priceCellNonQuoteLabel)}
                >
                    {messages.freeShippingLabel}
                </td>
            )}
        </tr>
    );
};
